<script setup lang="ts">
import { computed, ref, type PropType } from 'vue';
import Menu, { type MenuPassThroughMethodOptions } from 'primevue/menu';
import { EntryModel, FormModel } from '@/models';
import { ButtonType, CustomButton } from '@/components';

const props = defineProps({
  forms: {
    type: Object as PropType<FormModel[]>,
    required: true
  },
  entries: {
    type: Object as PropType<EntryModel[]>,
    required: true
  },
  marginLeft: {
    type: Number
  }
});

const types = ref();
const customButtonRef = ref<{ value: HTMLElement | null } | null>(null);

const menuItems = computed(() => {
  return props.forms.map((form, index) => ({
    label: form.title,
    index
  }));
});

const toggleMenu = (event: PointerEvent) => {
  types.value.toggle(event);
};

const emits = defineEmits(['onFormClick']);

const handleFormSelection = (index: number) => {
  if (!isEnforcingOrder(index) && !isOnlyOnceAndSubmitted(index)) {
    emits('onFormClick', props.forms[index]);
  }
};

const isOnlyOnceAndSubmitted = (index: number) => {
  if (index > -1 && hasFormEnforcements(index)) {
    const onlyOnce = props.forms[index].enforcements.onlyOnce;
    if (onlyOnce) {
      const formId = props.forms[index].identifier;
      const entry = props.entries.find(
        entryItem => entryItem.formIdentifier === formId
      );
      return entry !== undefined;
    }
  }
};

const hasFormEnforcements = (index: number) => {
  if (index > -1 && props.forms[index].enforcements) {
    const enforcements = props.forms[index].enforcements;
    return enforcements.enforceOrder || enforcements.hidden || enforcements.onlyOnce || enforcements.order > 0;
  }
  return false;
};

const isFormSubmittedAtLeastOnce = (index: number) => {
  if (index > -1 && hasFormEnforcements(index)) {
    const formId = props.forms[index].identifier;
    const entries = props.entries.filter(
      entryItem => entryItem.formIdentifier === formId
    );
    return entries.length > 0;
  }
  return false;
};

const isEnforcingOrder = (index: number) => {
  if (index > 0 && hasFormEnforcements(index)) {
    return props.forms[index].enforcements.enforceOrder;
  }
  return false;
};
</script>

<template>
  <div class="root">
    <CustomButton text="New entry"
      ref="customButtonRef"
      :type="ButtonType.POSITIVE"
      :margin-left="marginLeft"
      @onClick="toggleMenu"
      :icon="{ icon: 'file', style: 'far' }"/>
    <Menu :model="menuItems"
      :popup="true"
      :append-to="customButtonRef?.value"
      ref="types"
      :pt="{
        itemLink: (options: MenuPassThroughMethodOptions) =>({
          onClick: () => handleFormSelection(options.context.index)
        })
      }">
      <template #start>
        <div class="header">Choose a blank form</div>
      </template>
      <template #item="{ item, props }">
        <a v-ripple
          :class="isEnforcingOrder(item.index) ? `menu-enforce`: isFormSubmittedAtLeastOnce(item.index) ? 'menu-completed' : ''"
          v-if="!isOnlyOnceAndSubmitted(item.index)"
          v-bind="props.action"
          v-tooltip.left="
            isEnforcingOrder(item.index)
              ? `Please submit the form before this one from the list.`
              : ``
          ">
          <FontAwesomeIcon :icon="[`far`, 
            isEnforcingOrder(item.index) ?
              `file-lock` :
              isFormSubmittedAtLeastOnce(item.index) ?
              `file-circle-plus` :
              `file`
            ]"
            class="icon"/>
          <div class="type-label">{{ item.label }}</div>
        </a>
        <a v-ripple
          class="menu-completed"
          v-if="isOnlyOnceAndSubmitted(item.index)"
          v-bind="props.action"
          v-tooltip.left="'This form is already submitted, you can only submit it once.'">
          <FontAwesomeIcon :icon="[`fas`, 'file-circle-check']"
            class="icon"/>
          <div class="type-label">{{ item.label }}</div>
        </a>
      </template>
    </Menu>
  </div>
</template>

<style scoped lang="scss">
:deep(.p-menu, .p-menu-list) {
  max-height: 60vh;
  max-width: 400px;
  overflow-y: auto;
}
.header {
  padding: 12px 16px;
  color: #828e99;
}
.type-label {
  color: rgb(39, 42, 46);
  font-size: 14px;
  padding-left: 4px;
}
.icon {
  font-size: 16px;
}
.menu-enforce {
  cursor: not-allowed;
}
.menu-completed {
  color: #189988;
  cursor: not-allowed;

  .type-label {
    color: #189988;
  }
}
</style>
